import { useAtom, useSetAtom } from "jotai";
import { Board, CanvasTemplate, BlankCanvasTemplate, Permission, User } from "shared/datamodel/schemas";
import { State, Team } from "shared/datamodel/schemas/team";
import { appLoadingAtom, teamsAtom } from "state-atoms";
import { createProject as createProjectApi, deleteProject, editProject } from "frontend/services/projectsService";
import tracking from "frontend/tracking";
import consts, { SelectedTeamsAndProjects, TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY } from "shared/consts";
import useStateValue from "frontend/state/value";
import { addToLocalStorageArray, getLocalStorageItem, setLocalStorageItem } from "frontend/utils/storage-utils";
import { Project, UserProjectPermission } from "shared/datamodel/schemas/project";
import { moveBoard } from "frontend/services/boardsService";
import { useBoardsCache } from "frontend/hooks/caching/use-boards-cache";

export function useProject() {
  const setAppLoading = useSetAtom(appLoadingAtom);
  const [myTeams, setMyTeams] = useAtom(teamsAtom);
  const [{ user }, dispatch] = useStateValue();
  const { moveBoard: moveBoardCache } = useBoardsCache();

  const getSelectedTeam = (teamId: string): Team | undefined => {
    const selectedTeam = myTeams?.find((team: Team) => team.documentId === teamId);
    return selectedTeam;
  };

  async function createProject(name: string, users: User[] = [], teamId: string): Promise<Project> {
    const usersForDB: UserProjectPermission[] = users.map((user) => ({
      id: user.id,
      state: State.active,
      permission: user.permission,
    }));
    return await createProjectApi(name, usersForDB, teamId, dispatch);
  }

  function addProjectToSelectedTeam(project: Project, selectedTeam: Team) {
    const selectedTeamWithProject = {
      ...selectedTeam,
      projects: [...(selectedTeam?.projects?.filter((p) => p.documentId !== project.documentId) || []), project],
    };

    const filteredTeams = myTeams.filter((t) => t.documentId !== project.teamDocumentId);
    setMyTeams([...filteredTeams, selectedTeamWithProject]);

    const selectedTeamsAndProjects = getLocalStorageItem<SelectedTeamsAndProjects>(
      TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY
    );
    setLocalStorageItem(
      TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY,
      selectedTeamsAndProjects?.filter((item) => item.selectedTeam !== selectedTeam?.documentId)
    );

    addToLocalStorageArray(TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY, {
      selectedTeam: selectedTeam?.documentId,
      selectedProject: project.documentId,
    });
  }

  async function addCanvasesToProject(
    projectId: string,
    canvases: Board[],
    createNewBoard?: (template: CanvasTemplate, projectId?: string) => void,
    newCanvasForProject?: boolean
  ) {
    if (newCanvasForProject && createNewBoard) {
      createNewBoard(BlankCanvasTemplate, projectId);
    } else {
      await Promise.all(
        canvases.map((board) =>
          moveBoard(dispatch, { ...board, projectDocumentId: projectId, updatedAt: new Date() }, board)
        )
      );
    }
  }

  async function addCanvasesToProjectNew({
    projectId,
    canvases,
    teamId,
    createNewBoard,
    newCanvasForProject,
    setIsMovingBoards,
  }: {
    projectId: string;
    canvases: Board[];
    teamId: string;
    createNewBoard?: ({
      template,
      inputProjectId,
      inputTeamId,
    }: {
      template: CanvasTemplate;
      inputProjectId: string | null;
      inputTeamId: string | null;
      source: string;
    }) => void;
    newCanvasForProject?: boolean;
    setIsMovingBoards: (value: boolean) => void;
  }) {
    if (newCanvasForProject && createNewBoard) {
      createNewBoard({
        template: BlankCanvasTemplate,
        inputProjectId: projectId,
        inputTeamId: teamId,
        source: "new-project",
      });
    } else {
      await Promise.all(
        canvases.map((board) =>
          moveBoardCache(
            { ...board, projectDocumentId: projectId, updatedAt: new Date(), teamDocumentId: teamId },
            board
          )
        )
      );
      setIsMovingBoards(false);
    }
  }

  // create project -->handleCreateProjectWithoutAddingCanvases
  async function handleCreateProjectWithoutAddingCanvases(
    selectedTeam: Team,
    name: string,
    users: User[] = []
  ): Promise<Project | undefined> {
    setAppLoading(true);
    try {
      const projectFromDB = await createProject(name, users, selectedTeam.documentId);

      if (projectFromDB) {
        tracking.trackEvent(
          consts.TRACKING_CATEGORY.PROJECT_ACTION,
          "new_project_created",
          name,
          projectFromDB.accountId
        );

        addProjectToSelectedTeam(projectFromDB, selectedTeam);
        return projectFromDB;
      }
    } catch (error) {
      console.error(`Create Project Error for user ${user?.id}: `, error);
    } finally {
      setAppLoading(false);
    }
  }

  // create project --> name and teammates
  async function handleCreateProject(
    selectedTeam: Team,
    name: string,
    users: User[] = [],
    canvases: Board[] = [],
    createNewBoard?: (template: CanvasTemplate, projectId?: string) => void,
    newCanvasForProject?: boolean
  ) {
    setAppLoading(true);
    try {
      const projectFromDB = await createProject(name, users, selectedTeam.documentId);

      if (projectFromDB) {
        tracking.trackEvent(
          consts.TRACKING_CATEGORY.PROJECT_ACTION,
          "new_project_created",
          name,
          projectFromDB.accountId
        );

        addProjectToSelectedTeam(projectFromDB, selectedTeam);

        await addCanvasesToProject(projectFromDB.documentId, canvases, createNewBoard, newCanvasForProject);
      }
    } catch (error) {
      console.error(`Create Project Error for user ${user?.id}: `, error);
    } finally {
      setAppLoading(false);
    }
  }

  // delete project
  async function handleDeleteProject(project: Project) {
    setAppLoading(true);
    try {
      await deleteProject(project, dispatch);
      tracking.trackEvent(consts.TRACKING_CATEGORY.PROJECT_ACTION, "delete_project", project.name, project.accountId);

      const selectedTeam = getSelectedTeam(project.teamDocumentId);
      const filteredTeams = myTeams.filter((t) => t.documentId !== project.teamDocumentId);
      const filteredProjects = selectedTeam?.projects?.filter((p) => project.documentId !== p.documentId);

      const newSelectedTeam = {
        ...selectedTeam,
        projects: filteredProjects,
      };

      const selectedTeamsAndProjects = getLocalStorageItem<SelectedTeamsAndProjects>(
        TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY
      );
      setLocalStorageItem(
        TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY,
        selectedTeamsAndProjects?.filter((item) => item.selectedProject !== project.documentId)
      );

      addToLocalStorageArray(TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY, {
        selectedTeam: newSelectedTeam.documentId,
        selectedProject: filteredProjects && filteredProjects[0] ? filteredProjects[0]?.documentId : null,
      });

      setMyTeams([...filteredTeams, newSelectedTeam] as Team[]);
    } catch (error) {
      console.error(`Delete Project Error for user ${user?.id}: `, error);
    } finally {
      setAppLoading(false);
    }
  }

  // edit project name
  async function handleEditProjectName(name: string, project: Project) {
    try {
      const editedProject = await editProject(project, { name }, dispatch);
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.PROJECT_ACTION,
        "edit_project_name",
        project.name,
        project.accountId
      );

      const selectedTeam = getSelectedTeam(project.teamDocumentId);
      const filteredTeams = myTeams?.filter((team) => team.documentId !== selectedTeam?.documentId);

      const selectedTeamUpdateProjects =
        selectedTeam?.projects?.map((p) => (p.documentId === project.documentId ? editedProject : p)) ?? [];
      const newSelectedTeam = { ...selectedTeam, projects: selectedTeamUpdateProjects };

      setMyTeams([...filteredTeams, newSelectedTeam] as Team[]);
    } catch (error) {
      console.error(`Edit Project Name Error for user ${user?.id}: `, error);
    }
  }

  // edit members --> add and remove
  async function handleEditProjectMembers({
    project,
    users,
    state,
    showManageProjectModal = false,
  }: {
    project: Project;
    users: User[] | [];
    state: State | null;
    showManageProjectModal?: boolean;
  }) {
    if (!showManageProjectModal) {
      setAppLoading(true);
    }

    const selectedTeam = getSelectedTeam(project.teamDocumentId);

    try {
      const usersUpdatedState = users.map((user) => ({
        id: user.id,
        permission: user.permission ?? Permission.editor,
        state: state === State.deleted ? State.deleted : State.active,
      }));

      const editedProject = await editProject(project, { users: usersUpdatedState }, dispatch);

      if (editedProject) {
        tracking.trackEvent(
          consts.TRACKING_CATEGORY.PROJECT_ACTION,
          "edit_project_members",
          project.name,
          project.accountId
        );

        const filteredTeams = myTeams.filter((team) => team.documentId !== selectedTeam?.documentId);
        const selectedTeamUpdateProjects =
          selectedTeam?.projects?.map((p) => (p.documentId === project.documentId ? editedProject : p)) ?? [];
        const newSelectedTeam = { ...selectedTeam, projects: selectedTeamUpdateProjects };
        setMyTeams([...filteredTeams, newSelectedTeam] as Team[]);
      }
    } catch (error) {
      console.error(`Edit Project Members Error for user ${user?.id}: `, error);
    } finally {
      if (!showManageProjectModal) {
        setAppLoading(false);
      }
    }
  }

  // leave project
  async function handleLeaveProject(project: Project) {
    setAppLoading(true);
    const selectedTeam = getSelectedTeam(project.teamDocumentId);

    try {
      if (project.userProjectPermissions?.length === 1) {
        await handleDeleteProject(project);
      } else if (user) {
        const userPermission =
          project.userProjectPermissions?.find((teammate) => teammate.id.toString() === user.id)?.permission ??
          Permission.editor;
        const deletedPermission: UserProjectPermission = {
          id: user.id,
          state: State.deleted,
          permission: userPermission,
        };

        if (project.isOwner) {
          const newOwnerPermission = project.userProjectPermissions?.find(
            (teammate) => teammate.id.toString() !== user.id
          );
          const newOwnerId = newOwnerPermission?.id ?? null;

          if (newOwnerId) {
            await editProject(project, { users: [deletedPermission], newOwner: Number(newOwnerId) }, dispatch);
          }
        } else {
          await editProject(project, { users: [deletedPermission] }, dispatch);
        }

        tracking.trackEvent(consts.TRACKING_CATEGORY.PROJECT_ACTION, "leave_project", project.name, project.accountId);

        const filteredTeams = myTeams.filter((team) => team.documentId !== selectedTeam?.documentId);
        const filteredProjects = selectedTeam?.projects?.filter((p) => project.documentId !== p.documentId) ?? [];
        const newSelectedTeam = { ...selectedTeam, projects: filteredProjects };

        const selectedTeamsAndProjects = getLocalStorageItem<SelectedTeamsAndProjects>(
          TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY
        );
        setLocalStorageItem(
          TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY,
          selectedTeamsAndProjects?.filter(
            (item) =>
              item.selectedProject !== project.documentId && item.selectedProject !== filteredProjects[0]?.documentId
          )
        );

        addToLocalStorageArray(TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY, {
          selectedTeam: newSelectedTeam.documentId,
          selectedProject: filteredProjects[0]?.documentId ?? null,
        });

        setMyTeams([...filteredTeams, newSelectedTeam] as Team[]);
      }
    } catch (error) {
      console.error(`Leave Project Error for user ${user?.id}:`, error);
    } finally {
      setAppLoading(false);
    }
  }

  function isUserEditorInProject(project: Project, userId: string | undefined) {
    return project.userProjectPermissions.find((u) => {
      return String(u.id) === userId && u.permission === Permission.editor;
    });
  }

  function hasProjectEditorPermission(project: Project) {
    return project.isOwner || isUserEditorInProject(project, user?.id);
  }

  function getEditableProjectsForUser(team: Team): Project[] {
    return team.projects?.filter((p: Project) => hasProjectEditorPermission(p)) ?? [];
  }

  function getProjectName(projectId: string | null) {
    if (!projectId) return "";
    const project = getProjectById(projectId);
    if (!project) return "";
    return project.name;
  }

  function getProjectById(ProjectId: string): any {
    for (const team of myTeams) {
      const foundProject = team.projects?.find((project) => project.documentId === ProjectId);
      if (foundProject) {
        return foundProject;
      }
    }
    return null;
  }

  return {
    getSelectedTeam,
    handleCreateProject,
    handleDeleteProject,
    handleEditProjectName,
    handleEditProjectMembers,
    handleLeaveProject,
    getEditableProjectsForUser,
    getProjectName,
    hasProjectEditorPermission,
    getProjectById,
    handleCreateProjectWithoutAddingCanvases,
    addCanvasesToProject,
    addCanvasesToProjectNew,
  };
}
